import React from "react";
import type { Project } from "../../../../../../api/useProjects";
import { useQuantities } from "../../../../../../api/useQuantities";
import { usePost } from "../../../../../../api/useCall";
import type {
  CrossFilterParsingError,
  CrossFilterParsingResult,
} from "../../../../../../domain/parser/quantity/crossFilter/csv";
import { parse as parseCrossFilterFile } from "../../../../../../domain/parser/quantity/crossFilter/csv";
import t from "../../../../../../i18n";
import ModalForm from "@ui/Modal/Form";
import DropzoneV2 from "@ui/DropzoneV2";
import { isResultErr } from "../../../../../../lib/patterns/result";
import { getCrossFilterParsingErrorMessage } from "../../../../../../domain/parser/quantity/crossFilter/csv/errors";
import AdditionnalMessage from "@ui/AdditionnalMessage";

interface ImportCrossFilterProps {
  project: Project;
  onRequestClose: () => void;
}

export const ImportCrossFilterModalForm = ({ project, onRequestClose }: ImportCrossFilterProps) => {
  const projectId = project.id;
  const quantities = useQuantities(projectId);
  const setEndpoint = usePost("/projects/:projectId(uuid)/quantities/crossFiltersMetrology");

  const [filename, setFilename] = React.useState("");
  const [isValid, setIsValid] = React.useState<boolean>();
  const [errorText, setErrorText] = React.useState<string>();
  const [errorDetails, setErrorDetails] = React.useState<CrossFilterParsingError[]>();
  const [crossFilterParsingResult, setCrossFilterParsingResult] =
    React.useState<CrossFilterParsingResult>();

  const isCrossFilterValid = isValid && (crossFilterParsingResult?.data?.length || 0) > 0;

  const handleDrop = React.useCallback(
    async ([file]: File[]) => {
      if (!file) return;

      /**
       * Keep track of the file name to display inside the dropzone.
       */
      setFilename(file.name);

      /**
       * Parse file according to the expected format and content rules.
       */
      const result = await parseCrossFilterFile(file, {
        expectedQuantityNameValues: quantities.map(({ name, formula }) => ({ name, formula })),
      });

      /**
       * Catch for parsing errors.
       * Display humain readable error message and allow for downloading the error details.
       */
      if (isResultErr(result) && result.value[0]) {
        setIsValid(false);
        setErrorText(getCrossFilterParsingErrorMessage(result.value[0]));
        setErrorDetails(result.value);
        setCrossFilterParsingResult(undefined);
        return;
      }

      /**
       * Parsing was successful.
       * Save the result in the state to be used in the submit handler.
       */
      setIsValid(true);
      setCrossFilterParsingResult(result.value as CrossFilterParsingResult);
    },
    [quantities],
  );

  const downloadErrorDetails = React.useCallback(() => {
    const errors = JSON.stringify(errorDetails, null, 4);
    const a = document.createElement("a");
    a.setAttribute("href", URL.createObjectURL(new Blob([errors], { type: "application/json" })));
    a.setAttribute("download", `${new Date().toISOString()}_input_model_import_errors.json`);
    a.click();
  }, [errorDetails]);

  const handleSubmit = React.useCallback(async () => {
    if (crossFilterParsingResult) {
      await setEndpoint({
        routeParams: { projectId },
        body: {
          fileName: filename,
          crossFilters: crossFilterParsingResult.data,
        },
      });
    }

    onRequestClose();
    return true;
  }, [crossFilterParsingResult, setEndpoint, projectId, onRequestClose, filename]);

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={isCrossFilterValid ? handleSubmit : false}
      okButtonName={t("modalForm.button.name.import")}
      cancellable
    >
      <DropzoneV2
        onDrop={handleDrop}
        accept=".csv"
        nameFile={filename}
        validation={isValid}
        pathSampleFile="/metroscopia_example_cross_filter.csv"
        multiple={false}
        errorText={isValid ? undefined : errorText}
        downloadErrors={downloadErrorDetails}
      />
      <>
        {crossFilterParsingResult?.hasSimpleMetrology ? (
          <AdditionnalMessage
            status="INFO"
            text={t("quantities.cross_filter.modale.import.info.metrological_filters_are_ignored")}
          />
        ) : null}
      </>
      <AdditionnalMessage
        status="WARNING"
        text={t("quantities.cross_filter.modal.delete.additionnal_message.warning")}
      />
    </ModalForm>
  );
};
